import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../store/useAppStore';
import * as v from '../../utils/validation';
import * as e from '../../utils/encoding';
import { TextInput } from '../controls/TextInput';
import './ContentInputForm.css';

export const ContentInputForm: React.FC = () => {
  const { t } = useTranslation(['create', 'validation']);
  const payloadType = useAppStore(state => state.payloadType);
  const setContent = useAppStore(state => state.setContent);
  const scannedData = useAppStore(state => state.scannedData);
  const setScannedData = useAppStore(state => state.setScannedData);

  const [formData, setFormData] = useState<Record<string, string | undefined>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [touched, setTouched] = useState<Record<string, boolean | undefined>>({});

  useEffect(() => {
    setContent('');
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormData({});
    setErrors({});
    setTouched({});
  }, [payloadType, setContent]);

  useEffect(() => {
    if (scannedData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData(prev => ({ ...prev, ...scannedData }));
      setScannedData(null);
    }
  }, [scannedData, setScannedData]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };



  const runValidationAndEncode = useCallback(() => {
    let isValid = true;
    const newErrors: Record<string, string> = {};
    let encoded = '';

    const check = (res: v.ValidationResult, errKey: string) => {
      if (!res.isValid) {
        isValid = false;
        if (res.error) newErrors[errKey] = res.error;
      }
      return res;
    };

    switch (payloadType) {
      case 'url':
      case 'appstore':
      case 'social':
      case 'payment': {
        const url = formData.url ?? '';
        const res = check(v.validateURL(url), 'url');
        if (res.isValid && res.correctedValue !== undefined) encoded = e.encodeURL(res.correctedValue);
        break;
      }
      case 'text': {
        const text = formData.text ?? '';
        const res = check(v.validateText(text), 'text');
        if (res.isValid) encoded = e.encodeText(text);
        break;
      }
      case 'email': {
        const email = formData.email ?? '';
        const subject = formData.subject ?? '';
        const body = formData.body ?? '';
        const res = check(v.validateEmail(email), 'email');
        if (res.isValid) encoded = e.encodeEmail(email, subject, body);
        break;
      }
      case 'phone': {
        const phone = formData.phone ?? '';
        const res = check(v.validatePhone(phone), 'phone');
        if (res.isValid && res.correctedValue !== undefined) encoded = e.encodePhone(res.correctedValue);
        break;
      }
      case 'sms': {
        const phone = formData.phone ?? '';
        const body = formData.body ?? '';
        const res = check(v.validatePhone(phone), 'phone');
        // body length check? BRD says max 160
        if (body.length > 160) {
          isValid = false;
          newErrors.body = 'validation.sms.tooLong';
        }
        if (res.isValid && res.correctedValue !== undefined && isValid) encoded = e.encodeSMS(res.correctedValue, body);
        break;
      }
      case 'wifi': {
        const ssid = formData.ssid ?? '';
        const security = (formData.security as 'WPA' | 'WEP' | 'nopass' | undefined) ?? 'WPA';
        const password = formData.password ?? '';
        const hidden = formData.hidden === 'true';
        const res = check(v.validateWifi(ssid, security, password), 'wifi'); // wifi general err?
        if (!res.isValid && res.error) newErrors.ssid = res.error; // attach to ssid for now
        if (res.isValid) encoded = e.encodeWifi(ssid, security, password, hidden);
        break;
      }
      case 'vcard': {
        const fn = formData.fn ?? '';
        const res = check(v.validateVCard(fn), 'fn');
        if (res.isValid) {
          encoded = e.encodeVCard({
            fn,
            phone: formData.phone ?? '',
            email: formData.email ?? '',
            org: formData.org ?? '',
            title: formData.title ?? '',
            street: formData.street ?? '',
            city: formData.city ?? '',
            country: formData.country ?? '',
            url: formData.url ?? '',
            note: formData.note ?? '',
          });
        }
        break;
      }
      case 'calendar': {
        const title = formData.title ?? '';
        const start = formData.start ?? '';
        const end = formData.end ?? '';
        const location = formData.location ?? '';
        const desc = formData.description ?? '';
        const res = check(v.validateCalendar(title, start, end), 'calendar');
        if (!res.isValid && res.error) {
          if (res.error === 'validation.calendar.endBeforeStart' || res.error === 'validation.calendar.invalidEnd') {
            newErrors.end = res.error;
          } else if (res.error === 'validation.calendar.startRequired' || res.error === 'validation.calendar.invalidStart') {
            newErrors.start = res.error;
          } else {
            newErrors.title = res.error;
          }
        }
        if (res.isValid) encoded = e.encodeCalendar(title, start, end, location, desc);
        break;
      }
      case 'geo': {
        const lat = formData.lat ?? '';
        const lng = formData.lng ?? '';
        const res = check(v.validateGeo(lat, lng), 'geo');
        if (!res.isValid && res.error) newErrors.geo = res.error;
        if (res.isValid && res.correctedValue !== undefined) {
          encoded = e.encodeGeo(Number(lat), Number(lng));
        }
        break;
      }
    }

    setErrors(newErrors);
    
    // Only set content if we don't have errors and it's not totally empty (unless they cleared it)
    if (isValid && encoded) {
      setContent(encoded);
    } else {
      setContent('');
    }
  }, [formData, payloadType, setContent]);

  useEffect(() => {
    const timer = setTimeout(() => {
      runValidationAndEncode();
    }, 300); // Debounce
    return () => { clearTimeout(timer); };
  }, [formData, payloadType, runValidationAndEncode]);

  const renderError = (field: string) => {
    const errorKey = errors[field];
    const isTouched = field === 'geo' ? Boolean(touched.lat ?? touched.lng) : Boolean(touched[field]);
    if (isTouched && errorKey) {
      return <div id={`${field}-error`} className="field-error">{t(errorKey, { ns: 'validation' })}</div>;
    }
    return null;
  };

  const getFieldState = (id: string): 'idle' | 'valid' | 'invalid' => {
    if (id === 'lat' || id === 'lng') {
      if (!touched[id]) return 'idle';
      return errors.geo ? 'invalid' : 'valid';
    }
    if (!touched[id]) return 'idle';
    return errors[id] ? 'invalid' : 'valid';
  };

  const renderInput = (id: string, label: string, type = 'text', maxLength?: number) => {
    const val = formData[id] ?? '';
    const state = getFieldState(id);
    return (
      <div className="flex-col-gap-4">
        <TextInput
          id={id}
          label={label}
          type={type}
          isTextArea={type === 'textarea'}
          value={val}
          maxLength={maxLength}
          state={state}
          onChange={(e) => { handleChange(id, e.target.value); }}
          onBlur={() => { handleBlur(id); }}
          {...(type === 'textarea' ? { rows: 4 } : {})}
        />
        {renderError(id)}
      </div>
    );
  };

  // Rendering form based on payloadType
  const renderFormFields = () => {
    switch (payloadType) {
      case 'url':
      case 'appstore':
      case 'social':
      case 'payment':
        return renderInput('url', t(`create.forms.${payloadType}.urlLabel`, 'URL'), 'url', 2048);
      case 'text':
        return renderInput('text', t('create.forms.text.label', 'Text Content'), 'textarea', 2000);
      case 'email':
        return (
          <>
            {renderInput('email', t('create.forms.email.label', 'Email Address'), 'email', 254)}
            {renderInput('subject', t('create.forms.email.subject', 'Subject (Optional)'))}
            {renderInput('body', t('create.forms.email.body', 'Message Body (Optional)'), 'textarea')}
          </>
        );
      case 'phone':
        return renderInput('phone', t('create.forms.phone.label', 'Phone Number'), 'tel');
      case 'sms':
        return (
          <>
            {renderInput('phone', t('create.forms.phone.label', 'Phone Number'), 'tel')}
            {renderInput('body', t('create.forms.sms.bodyLabel', 'Message Body'), 'textarea', 160)}
          </>
        );
      case 'wifi':
        return (
          <>
            {renderInput('ssid', t('create.forms.wifi.ssid', 'Network Name (SSID)'))}
            <div className="input-group">
              <label htmlFor="wifi-security">{t('create.forms.wifi.security', 'Security Type')}</label>
              <select id="wifi-security" value={formData.security ?? 'WPA'} onChange={(e) => { handleChange('security', e.target.value); }}>
                <option value="WPA">WPA/WPA2/WPA3</option>
                <option value="WEP">WEP</option>
                <option value="nopass">{t('create.forms.wifi.none', 'None')}</option>
              </select>
            </div>
            {formData.security !== 'nopass' && renderInput('password', t('create.forms.wifi.password', 'Password'), 'password')}
            <div className="checkbox-group">
              <input type="checkbox" id="hidden" checked={formData.hidden === 'true'} onChange={(e) => { handleChange('hidden', e.target.checked ? 'true' : 'false'); }} />
              <label htmlFor="hidden">{t('create.forms.wifi.hidden', 'Hidden Network')}</label>
            </div>
            {renderError('wifi')}
          </>
        );
      case 'vcard':
        return (
          <>
            {renderInput('fn', t('create.forms.vcard.fn', 'Full Name *'))}
            {renderInput('phone', t('create.forms.vcard.phone', 'Phone Number'), 'tel')}
            {renderInput('email', t('create.forms.vcard.email', 'Email Address'), 'email')}
            {renderInput('org', t('create.forms.vcard.org', 'Organization'))}
            {renderInput('title', t('create.forms.vcard.title', 'Job Title'))}
            {renderInput('street', t('create.forms.vcard.street', 'Street Address'))}
            {renderInput('city', t('create.forms.vcard.city', 'City'))}
            {renderInput('country', t('create.forms.vcard.country', 'Country'))}
            {renderInput('url', t('create.forms.vcard.url', 'Website URL'), 'url')}
            {renderInput('note', t('create.forms.vcard.note', 'Note'), 'textarea')}
          </>
        );
      case 'calendar':
        return (
          <>
            {renderInput('title', t('create.forms.calendar.title', 'Event Title *'))}
            {renderInput('start', t('create.forms.calendar.start', 'Start Date & Time *'), 'datetime-local')}
            {renderInput('end', t('create.forms.calendar.end', 'End Date & Time'), 'datetime-local')}
            {renderInput('location', t('create.forms.calendar.location', 'Location'))}
            {renderInput('description', t('create.forms.calendar.description', 'Description'), 'textarea')}
            {renderError('calendar')}
          </>
        );
      case 'geo':
        return (
          <>
            {renderInput('lat', t('create.forms.geo.lat', 'Latitude (e.g. 37.7749)'), 'number')}
            {renderInput('lng', t('create.forms.geo.lng', 'Longitude (e.g. -122.4194)'), 'number')}
            {renderError('geo')}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="content-input-form">
      {renderFormFields()}
    </div>
  );
};
